import { useEffect, useMemo, useState } from "react";
import { useOutletContext, useParams, Navigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Download, Eye, EyeOff, Search } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { http, apiError, API } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { SCHEMAS, EDITION_SCOPED } from "@/admin/schema";
import Field from "@/admin/Field";

export default function AdminCollection() {
  const { collection } = useParams();
  const { isAdmin, editionId, reloadEditions } = useOutletContext();
  const site = useSite();
  const schema = SCHEMAS[collection];
  const scoped = EDITION_SCOPED.includes(collection);
  const [items, setItems] = useState([]);
  const [refs, setRefs] = useState({});
  const [form, setForm] = useState(null);
  const [del, setDel] = useState(null);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  const params = scoped ? { edition_id: editionId } : {};
  const load = () => http.get(`/admin/${collection}`, { params }).then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => {
    if (!schema || (scoped && !editionId)) return;
    load();
    const needed = [...new Set(schema.fields.filter((f) => f.ref).map((f) => f.ref))];
    Promise.all(needed.map((n) => http.get(`/admin/${n}`, { params: EDITION_SCOPED.includes(n) ? { edition_id: editionId } : {} }).then((r) => [n, r.data])))
      .then((pairs) => setRefs(Object.fromEntries(pairs)));
  }, [collection, editionId]); // eslint-disable-line react-hooks/exhaustive-deps

  const list = useMemo(() => items.filter((i) => (i[schema?.title] || "").toLowerCase().includes(q.toLowerCase())), [items, q, schema]);
  if (!schema) return <Navigate to="/admin" replace />;
  if (schema.adminOnly && !isAdmin) return <p>Acesso reservado a administradores.</p>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const after = () => { load(); site.reload(); if (collection === "editions") reloadEditions(); };
  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    const body = { ...form, ...(scoped ? { edition_id: editionId } : {}) };
    try {
      if (form.id) await http.put(`/admin/${collection}/${form.id}`, body); else await http.post(`/admin/${collection}`, body);
      toast.success(isAdmin ? "Guardado" : "Guardado como rascunho — aguarda publicação por um administrador");
      setForm(null); after();
    } catch (err) { toast.error(apiError(err)); } finally { setSaving(false); }
  };
  const togglePub = async (it) => {
    try { await http.put(`/admin/${collection}/${it.id}`, { ...it, published: !it.published }); toast.success(it.published ? "Despublicado" : "Publicado"); after(); } catch (err) { toast.error(apiError(err)); }
  };
  const remove = async () => {
    try { await http.delete(`/admin/${collection}/${del.id}`); toast.success("Eliminado"); setDel(null); after(); } catch (err) { toast.error(apiError(err)); }
  };
  const exportUrl = `${API}/admin/export/${collection}${scoped ? `?edition_id=${editionId}` : ""}`;

  return (
    <div data-testid={`admin-collection-${collection}`}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><h1 className="font-display text-3xl font-extrabold">{schema.label}</h1><p className="text-sm text-slate-600">{items.length} registo{items.length === 1 ? "" : "s"}{scoped && " nesta edição"}</p></div>
        <div className="flex gap-2">
          <a href={exportUrl} className="h-11 px-4 rounded-full border bg-white inline-flex items-center gap-2 text-sm font-semibold" data-testid="admin-export-csv"><Download className="h-4 w-4" />Exportar CSV</a>
          <button onClick={() => setForm({ published: isAdmin, ...(schema.defaults || {}) })} className="h-11 px-4 rounded-full bg-[var(--leaf)] text-white inline-flex items-center gap-2 text-sm font-semibold" data-testid="admin-new-button"><Plus className="h-4 w-4" />Novo</button>
        </div>
      </div>
      {!isAdmin && <p className="mt-4 rounded-xl bg-amber-100 p-3 text-sm text-amber-900">Como editor, pode criar e editar conteúdos. A publicação e a eliminação são feitas por um administrador.</p>}
      <label className="relative block mt-6 max-w-md"><span className="sr-only">Pesquisar</span><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquisar" className="h-11 w-full rounded-lg border bg-white pl-9 pr-3 text-sm" data-testid="admin-search-input" /></label>
      <ul className="mt-4 divide-y rounded-2xl border bg-white">
        {list.map((it) => (
          <li key={it.id} className="flex items-center gap-3 p-3 sm:p-4" data-testid={`admin-row-${it.slug || it.id}`}>
            <div className="min-w-0 flex-1">
              <p className="font-semibold truncate">{it[schema.title]}</p>
              <p className="text-xs text-slate-500 truncate">{schema.sub?.(it)} {it.demo && "· fictício"} {it.featured && "· destaque"} {it.status && it.status !== "agendada" && `· ${it.status}`}</p>
            </div>
            <span className={`text-xs font-bold rounded-full px-2.5 py-1 ${it.published ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}`}>{it.published ? "Publicado" : "Rascunho"}</span>
            {isAdmin && <button onClick={() => togglePub(it)} className="h-10 w-10 grid place-items-center rounded-lg hover:bg-black/5" aria-label={it.published ? "Despublicar" : "Publicar"} data-testid={`admin-toggle-publish-${it.slug || it.id}`}>{it.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>}
            <button onClick={() => setForm({ ...it })} className="h-10 w-10 grid place-items-center rounded-lg hover:bg-black/5" aria-label="Editar" data-testid={`admin-edit-${it.slug || it.id}`}><Pencil className="h-4 w-4" /></button>
            {isAdmin && <button onClick={() => setDel(it)} className="h-10 w-10 grid place-items-center rounded-lg hover:bg-red-50 text-red-700" aria-label="Eliminar" data-testid={`admin-delete-${it.slug || it.id}`}><Trash2 className="h-4 w-4" /></button>}
          </li>
        ))}
        {!list.length && <li className="p-6 text-sm text-slate-600">Sem registos.</li>}
      </ul>

      <Sheet open={!!form} onOpenChange={(o) => !o && setForm(null)}>
        <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto bg-white">
          <SheetTitle className="font-display text-2xl">{form?.id ? "Editar" : "Novo"} · {schema.label}</SheetTitle>
          {form && (
            <form onSubmit={save} className="mt-6 space-y-4" data-testid="admin-form">
              {schema.fields.map((f) => <Field key={f.key} f={f} form={form} set={set} refs={refs} isAdmin={isAdmin} />)}
              <div className="sticky bottom-0 bg-white py-4 flex gap-2">
                <button disabled={saving} className="h-12 px-6 rounded-full bg-[var(--leaf)] text-white font-bold disabled:opacity-60" data-testid="admin-form-save">{saving ? "A guardar…" : "Guardar"}</button>
                <button type="button" onClick={() => setForm(null)} className="h-12 px-6 rounded-full border font-semibold" data-testid="admin-form-cancel">Cancelar</button>
              </div>
            </form>
          )}
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent>
          <AlertDialogTitle>Eliminar “{del?.[schema.title]}”?</AlertDialogTitle>
          <AlertDialogDescription>Esta ação não pode ser anulada.</AlertDialogDescription>
          <AlertDialogFooter><AlertDialogCancel data-testid="admin-delete-cancel">Cancelar</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-red-700" data-testid="admin-delete-confirm">Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
